/**
 * BuyerJourneyModel (AJIP-16.02.007 · WS3, WS4, WS7, WS8)
 * 14 stages, 18 events, deterministic transition table, stage→state map.
 */
import { STATES } from '../core/JourneyState';

const BUYER_STAGES = Object.freeze({
  INTENT_CAPTURED: 'INTENT_CAPTURED',
  REQUIREMENTS_DEFINED: 'REQUIREMENTS_DEFINED',
  DISCOVERY_READY: 'DISCOVERY_READY',
  OPTIONS_IDENTIFIED: 'OPTIONS_IDENTIFIED',
  OPTIONS_EVALUATED: 'OPTIONS_EVALUATED',
  SUPPLIER_SHORTLISTED: 'SUPPLIER_SHORTLISTED',
  ENGAGEMENT_READY: 'ENGAGEMENT_READY',
  RFQ_READY: 'RFQ_READY',
  RFQ_INITIATED: 'RFQ_INITIATED',
  RESPONSE_PENDING: 'RESPONSE_PENDING',
  RESPONSE_RECEIVED: 'RESPONSE_RECEIVED',
  DECISION_READY: 'DECISION_READY',
  ENGAGEMENT_COMPLETED: 'ENGAGEMENT_COMPLETED',
  JOURNEY_COMPLETED: 'JOURNEY_COMPLETED',
});
const ALL_BUYER_STAGES = Object.freeze(Object.values(BUYER_STAGES));

const BUYER_EVENTS = Object.freeze({
  BUYER_INTENT_CAPTURED: 'BUYER_INTENT_CAPTURED',
  BUYER_REQUIREMENTS_DEFINED: 'BUYER_REQUIREMENTS_DEFINED',
  BUYER_DISCOVERY_READY: 'BUYER_DISCOVERY_READY',
  BUYER_OPTIONS_IDENTIFIED: 'BUYER_OPTIONS_IDENTIFIED',
  BUYER_OPTIONS_EVALUATED: 'BUYER_OPTIONS_EVALUATED',
  BUYER_SUPPLIER_SHORTLISTED: 'BUYER_SUPPLIER_SHORTLISTED',
  BUYER_ENGAGEMENT_READY: 'BUYER_ENGAGEMENT_READY',
  BUYER_RFQ_READY: 'BUYER_RFQ_READY',
  BUYER_RFQ_INITIATED: 'BUYER_RFQ_INITIATED',
  BUYER_RESPONSE_PENDING: 'BUYER_RESPONSE_PENDING',
  BUYER_RESPONSE_RECEIVED: 'BUYER_RESPONSE_RECEIVED',
  BUYER_DECISION_READY: 'BUYER_DECISION_READY',
  BUYER_ENGAGEMENT_COMPLETED: 'BUYER_ENGAGEMENT_COMPLETED',
  BUYER_JOURNEY_COMPLETED: 'BUYER_JOURNEY_COMPLETED',
  BUYER_JOURNEY_BLOCKED: 'BUYER_JOURNEY_BLOCKED',
  BUYER_JOURNEY_CANCELLED: 'BUYER_JOURNEY_CANCELLED',
  BUYER_JOURNEY_FAILED: 'BUYER_JOURNEY_FAILED',
  BUYER_JOURNEY_RESUMED: 'BUYER_JOURNEY_RESUMED',
});
const ALL_BUYER_EVENTS = Object.freeze(Object.values(BUYER_EVENTS));

const STAGE_TO_ENGINE_STATE = Object.freeze({
  INTENT_CAPTURED: STATES.STARTED,
  REQUIREMENTS_DEFINED: STATES.IN_PROGRESS,
  DISCOVERY_READY: STATES.IN_PROGRESS,
  OPTIONS_IDENTIFIED: STATES.IN_PROGRESS,
  OPTIONS_EVALUATED: STATES.IN_PROGRESS,
  SUPPLIER_SHORTLISTED: STATES.IN_PROGRESS,
  ENGAGEMENT_READY: STATES.IN_PROGRESS,
  RFQ_READY: STATES.IN_PROGRESS,
  RFQ_INITIATED: STATES.IN_PROGRESS,
  RESPONSE_PENDING: STATES.WAITING,
  RESPONSE_RECEIVED: STATES.IN_PROGRESS,
  DECISION_READY: STATES.IN_PROGRESS,
  ENGAGEMENT_COMPLETED: STATES.IN_PROGRESS,
  JOURNEY_COMPLETED: STATES.COMPLETED,
});

// Stage transitions authorising which buyer event advances from → to.
// Persistence flag indicates authorised checkpoint per WS12.
const BUYER_STAGE_TRANSITIONS = Object.freeze([
  { from: BUYER_STAGES.INTENT_CAPTURED, event: BUYER_EVENTS.BUYER_REQUIREMENTS_DEFINED, to: BUYER_STAGES.REQUIREMENTS_DEFINED, persist: true },
  { from: BUYER_STAGES.REQUIREMENTS_DEFINED, event: BUYER_EVENTS.BUYER_DISCOVERY_READY, to: BUYER_STAGES.DISCOVERY_READY, persist: true },
  { from: BUYER_STAGES.DISCOVERY_READY, event: BUYER_EVENTS.BUYER_OPTIONS_IDENTIFIED, to: BUYER_STAGES.OPTIONS_IDENTIFIED, persist: true },
  { from: BUYER_STAGES.OPTIONS_IDENTIFIED, event: BUYER_EVENTS.BUYER_OPTIONS_EVALUATED, to: BUYER_STAGES.OPTIONS_EVALUATED, persist: true },
  { from: BUYER_STAGES.OPTIONS_EVALUATED, event: BUYER_EVENTS.BUYER_SUPPLIER_SHORTLISTED, to: BUYER_STAGES.SUPPLIER_SHORTLISTED, persist: true },
  { from: BUYER_STAGES.SUPPLIER_SHORTLISTED, event: BUYER_EVENTS.BUYER_ENGAGEMENT_READY, to: BUYER_STAGES.ENGAGEMENT_READY, persist: true },
  { from: BUYER_STAGES.ENGAGEMENT_READY, event: BUYER_EVENTS.BUYER_RFQ_READY, to: BUYER_STAGES.RFQ_READY, persist: true },
  { from: BUYER_STAGES.RFQ_READY, event: BUYER_EVENTS.BUYER_RFQ_INITIATED, to: BUYER_STAGES.RFQ_INITIATED, persist: true },
  { from: BUYER_STAGES.RFQ_INITIATED, event: BUYER_EVENTS.BUYER_RESPONSE_PENDING, to: BUYER_STAGES.RESPONSE_PENDING, persist: true, engineTransition: 'wait' },
  { from: BUYER_STAGES.RESPONSE_PENDING, event: BUYER_EVENTS.BUYER_RESPONSE_RECEIVED, to: BUYER_STAGES.RESPONSE_RECEIVED, persist: true, engineTransition: 'resume' },
  { from: BUYER_STAGES.RESPONSE_RECEIVED, event: BUYER_EVENTS.BUYER_DECISION_READY, to: BUYER_STAGES.DECISION_READY, persist: true },
  { from: BUYER_STAGES.DECISION_READY, event: BUYER_EVENTS.BUYER_ENGAGEMENT_COMPLETED, to: BUYER_STAGES.ENGAGEMENT_COMPLETED, persist: true },
  { from: BUYER_STAGES.ENGAGEMENT_COMPLETED, event: BUYER_EVENTS.BUYER_JOURNEY_COMPLETED, to: BUYER_STAGES.JOURNEY_COMPLETED, persist: true, engineTransition: 'complete' },
]);

const findBuyerTransition = (fromStage, event) =>
  BUYER_STAGE_TRANSITIONS.find((t) => t.from === fromStage && t.event === event) || null;

export {
  BUYER_STAGES, ALL_BUYER_STAGES,
  BUYER_EVENTS, ALL_BUYER_EVENTS,
  STAGE_TO_ENGINE_STATE, BUYER_STAGE_TRANSITIONS,
  findBuyerTransition,
};
